import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, MapPin } from "lucide-react";
import { Meta, Schema, Breadcrumbs, buildTitle, buildDescription } from "../components/SEO";
import { findService, services } from "../data/services";
import { findCity, isPriorityCity } from "../data/cities";
import { usePublicProjects } from "../hooks/useProjects";
import { ReviewsSection, WhyChoose, CtaStrip } from "../components/Sections";
import { QuoteForm } from "../components/QuoteForm";
import { FaqAccordion } from "../components/FaqAccordion";
import { ServiceRichContent } from "../components/ServiceRichContent";
import { ConfiguratorCTA } from "../components/ConfiguratorCTA";
import { SmartImage } from "../components/SmartImage";
import { getContent } from "../data/serviceContent";
import { getBadkamerLocal } from "../data/content/badkamerLocal";
import { ServicePhotoGallery } from "../components/ServicePhotoGallery";

export default function ServiceCityPage() {
  const { slug, city } = useParams();
  const service = findService(slug);
  const place = findCity(city);
  const { allProjects } = usePublicProjects();
  if (!service || !place) return <Navigate to="/diensten" replace />;

  const local = service.slug === "badkamer-renovatie" ? getBadkamerLocal(place.slug) : null;

  const cityFaqs = [
    ...(local?.faqs || []),
    {
      q: `Doen jullie ${service.title.toLowerCase()} in heel ${place.name}?`,
      a: `Ja. Wij zijn actief in alle wijken van ${place.name}, waaronder ${place.neighborhoods.slice(0, 4).join(", ")} en ${place.neighborhoods.slice(-1)}.`,
    },
    {
      q: `Hoeveel kost ${service.title.toLowerCase()} in ${place.name}?`,
      a: `Wij werken uitsluitend met een vaste totaalprijs op basis van opname. De uiteindelijke investering hangt af van afmetingen, materialen en gewenste afwerking. Vraag een vrijblijvende prijsindicatie aan — u ontvangt binnen één werkdag persoonlijk contact.`,
    },
    {
      q: `Hoe snel kunnen jullie starten in ${place.name}?`,
      a: `Doorgaans kunnen wij binnen 4–8 weken starten met een project in ${place.name}. Voor spoedklussen zoeken wij naar versnelde planning.`,
    },
    ...service.faqs.slice(0, 3),
  ];

  const sameService = allProjects.filter((p) => p.service_slug === service.slug);
  const showProjects = [
    ...sameService.filter((p) => p.city === place.name),
    ...sameService.filter((p) => p.city !== place.name),
    ...allProjects.filter((p) => p.service_slug !== service.slug && p.city === place.name),
    ...allProjects.filter((p) => p.service_slug !== service.slug && p.city !== place.name),
  ].slice(0, 3);
  const hasLocalProjects = sameService.some((p) => p.city === place.name);

  // Hybrid SEO: shared rich content + local intro/outro per city
  const baseRichContent = getContent(service.slug);
  const richContent = baseRichContent
    ? {
        ...baseRichContent,
        longIntro: [
          `Op zoek naar een specialist in ${service.title.toLowerCase()} in ${place.name}? Koning Aannemersbedrijf voert deze projecten uit in ${place.name} en omgeving, van ${place.neighborhoods.slice(0, 3).join(", ")} tot ${place.neighborhoods[place.neighborhoods.length - 1]}. Wij combineren kennis van de typische woningvoorraad in ${place.name} met landelijke kwaliteit­standaarden, vaste prijzen en vakwerk volgens de VCA-veiligheidsnormen.`,
          ...(baseRichContent.longIntro || []),
        ],
        related: [
          `${service.keyword} ${place.name.toLowerCase()}`,
          `${service.title.toLowerCase()} ${place.name.toLowerCase()}`,
          `aannemer ${place.name.toLowerCase()}`,
          `${service.keyword} aannemer ${place.name.toLowerCase()}`,
          ...(baseRichContent.related || []),
        ],
      }
    : null;

  const serviceCitySchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${service.title} in ${place.name}`,
    serviceType: service.title,
    areaServed: { "@type": "City", name: place.name },
    provider: { "@type": "GeneralContractor", name: "Koning Aannemersbedrijf" },
  };

  return (
    <>
      <Meta
        title={buildTitle(local ? `Badkamer Renovatie ${place.name} | Vast team, vaste prijs` : `${service.title} ${place.name}`)}
        description={buildDescription(local
          ? `Badkamer laten verbouwen in ${place.name}? Eigen loodgieter, elektricien en tegelzetter in één vast team. Vaste prijs na gratis opname, uitvoering in ca. 2 weken.`
          : `${service.title} in ${place.name}? Lokale aannemer met vast team, vaste prijs en VCA-gecertificeerde medewerkers. Binnen één werkdag persoonlijk contact.`)}
        noindex={!isPriorityCity(place.slug)}
      />
      <Schema data={serviceCitySchema} />
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "Diensten", url: "/diensten" },
        { name: service.title, url: `/diensten/${service.slug}` },
        { name: place.name, url: `/diensten/${service.slug}/${place.slug}` },
      ]} />

      <section className="relative min-h-[65vh] flex items-end overflow-hidden grain" data-testid="service-city-hero">
        <SmartImage src={service.image} alt={`${service.title} ${place.name}`} sizes="100vw" eager className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative w-full max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-xs tracking-[0.28em] uppercase text-koning-gold mb-4 flex items-center gap-2 flex-wrap">
              <Link to="/diensten" className="hover:underline">Diensten</Link>
              <span>·</span>
              <Link to={`/diensten/${service.slug}`} className="hover:underline">{service.title}</Link>
              <span>·</span>
              <span>{place.name}</span>
            </div>
            <h1 className="font-display text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              {service.title} <span className="italic text-koning-gold">{place.name}</span>
            </h1>
            <p className="mt-6 text-koning-text-2/90 max-w-xl leading-relaxed">
              Specialist in {service.title.toLowerCase()} in {place.name}. Wij combineren vakmanschap met strakke planning, vaste prijzen en lokale ervaring in wijken als {place.neighborhoods.slice(0, 3).join(", ")}.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/offerte" className="btn-gold" data-testid="service-city-cta-offerte">Vraag offerte aan <ArrowUpRight size={16} /></Link>
              <a href="tel:+31101234567" className="btn-outline">Bel direct</a>
            </div>
          </div>
        </div>
      </section>

      {/* RECENT PROJECTS — direct onder de hero: klanten kijken eerst naar foto's */}
      {showProjects.length > 0 && (
      <section className="py-16 md:py-24 bg-[#0B0B0B] border-y border-koning-border" data-testid="service-city-projects">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow mb-3">Projecten in {place.name}</div>
              <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight">{hasLocalProjects ? `Recent gerealiseerd in ${place.name}` : `Recent gerealiseerd: ${service.title.toLowerCase()}`}</h2>
              <p className="mt-3 text-koning-muted max-w-xl">Echte projecten van ons eigen team — klik op een project voor alle foto's en de vóór/ná-vergelijking.</p>
            </div>
            <Link to="/projecten" className="btn-outline">Alle projecten <ArrowUpRight size={16} /></Link>
          </div>
          <div className={`grid grid-cols-1 gap-6 ${showProjects.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
            {showProjects.map((p) => (
              <Link key={p.slug} to={`/projecten/${p.slug}`} className="group img-zoom block border border-koning-border hover:border-koning-gold/60 transition-colors">
                <div className="relative aspect-[4/5]">
                  <SmartImage src={p.cover} alt={p.title} aspect="4/5" sizes="(max-width: 768px) 100vw, 33vw" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-koning-bg/95 via-koning-bg/30 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <div className="text-koning-gold text-[10px] tracking-[0.24em] uppercase">{p.category} · {p.city}</div>
                    <h3 className="font-display text-white text-xl mt-2">{p.title}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Local content — premium licht */}
      <section className="py-20 md:py-28 bg-[#F5F2EA] border-b border-[#E1D9C5]" data-testid="service-city-intro">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-[1.2fr_1fr] gap-14">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">{service.title} · {place.name}</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight max-w-2xl text-[#1A1816]">
              Uw lokale specialist in <span className="italic text-[#8A7430]">{place.name}</span>
            </h2>
            <p className="mt-6 text-[#4A453E] leading-relaxed max-w-2xl">
              Koning Aannemersbedrijf is al jaren een vertrouwd gezicht in {place.name}. Of u nu woont in {place.neighborhoods[0]}, {place.neighborhoods[1]} of {place.neighborhoods[2] || place.neighborhoods[0]} — wij realiseren {service.title.toLowerCase()} projecten met oog voor de typische bouwwijze, leeftijd en details van uw woning. Wij combineren lokale ervaring met landelijke kwaliteit­standaarden.
            </p>
            {local ? (
              <div className="mt-4 space-y-4 max-w-2xl" data-testid="service-city-local-content">
                {local.intro.map((p) => <p key={p.slice(0, 60)} className="text-[#4A453E] leading-relaxed">{p}</p>)}
              </div>
            ) : place.localContext && (
              <p className="mt-4 text-[#6F6A60] leading-relaxed max-w-2xl">{place.localContext}</p>
            )}
            <ul className="mt-8 space-y-4">
              {service.bullets?.map((b) => (
                <li key={b} className="flex items-start gap-4 border-b border-[#E1D9C5] pb-4">
                  <Check size={20} className="text-[#8A7430] mt-1 flex-shrink-0" />
                  <span className="text-[#1A1816] text-base md:text-lg">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 p-6 bg-white border border-[#E1D9C5] shadow-premium">
              <div className="text-xs tracking-[0.24em] uppercase text-[#8A7430] mb-2">Prijs {service.title.toLowerCase()} in {place.name}</div>
              <p className="text-[#1A1816] text-base md:text-lg leading-relaxed">
                <span className="font-display text-2xl text-[#8A7430]">Offerte op maat.</span>{" "}
                <span className="text-[#6F6A60]">Vaste totaalprijs op basis van een opname bij u thuis. Vraag een vrijblijvende prijsindicatie aan.</span>
              </p>
            </div>

            <div className="mt-8 flex items-start gap-3 text-[#6F6A60] text-sm">
              <MapPin className="text-[#8A7430] mt-0.5" size={16} />
              <span>Werkzaam in: {place.neighborhoods.join(", ")}.</span>
            </div>
          </div>

          <div className="lg:sticky lg:top-24 self-start">
            <QuoteForm initialService={service.title} compact />
          </div>
        </div>
      </section>

      <ServicePhotoGallery serviceSlug={service.slug} serviceTitle={service.title} city={place.name} />

      <WhyChoose variant="light" items={service.why} />

      {["aanbouw", "uitbouw"].includes(service.slug) && <ConfiguratorCTA city={place.name} />}

      {/* RICH SEO CONTENT — 1500+ words, with local intro */}
      <ServiceRichContent content={richContent} />

      <ReviewsSection limit={3} title="Wat klanten zeggen" variant="elevated" />

      {/* FAQ — licht beige */}
      <section className="py-20 md:py-28 bg-[#F5F2EA] border-y border-[#E1D9C5]" data-testid="service-city-faq">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Veelgestelde vragen</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-10 text-[#1A1816]">
            {service.title} in {place.name}
          </h2>
          <FaqAccordion items={cityFaqs} variant="light" />
        </div>
      </section>

      <CtaStrip
        variant="gold"
        title={`Klaar voor uw ${service.title.toLowerCase()} in ${place.name}?`}
        subtitle="Binnen één werkdag persoonlijk contact. Lokale kennis, premium kwaliteit."
      />

      {/* Other services in this city */}
      <section className="py-12 border-t border-koning-border" data-testid="other-services-city">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-xs tracking-[0.24em] uppercase text-koning-muted mb-4">Andere diensten in {place.name}</div>
          <div className="flex flex-wrap gap-2">
            {services.filter((s) => s.slug !== service.slug).slice(0, 12).map((s) => (
              <Link key={s.slug} to={`/diensten/${s.slug}/${place.slug}`} className="px-4 py-2 border border-koning-border text-sm text-koning-muted hover:text-koning-gold hover:border-koning-gold transition-colors">
                {s.title} {place.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
